import { TT_MODEL_BASE_URL } from '../../config/api';

// Types matching the Java DTOs (meta-model TtMarketsRequest/TtMarketsResponse)
export type TtMatchFormat = 'BO3' | 'BO5';

/** Final points of one finished set. */
export interface TtSetPoints {
  pointsA: number;
  pointsB: number;
}

export interface TtMarketsRequest {
  /** Finished sets in the order they were played; the set score and points played follow from these. */
  completedSets: TtSetPoints[];
  pointsA: number;
  pointsB: number;
  format: TtMatchFormat;
  pointWinProb: number; // P(A wins a single point), 0..1
  /** Point targets to price races to; omitted asks for the backend's own set, an empty list for none. */
  raceTargets?: number[];
  /** Point numbers to price the point winner on; read the same way the race targets above are. */
  pointNumbers?: number[];
}

export interface TtDerivedState {
  setsA: number;
  setsB: number;
  pointsAlreadyPlayed: number;
}

export interface TtTwoWayOdds {
  probA: number;
  probB: number;
  fairPriceA: number;
  fairPriceB: number;
}

export interface TtSetOdds extends TtTwoWayOdds {
  set: number;
  /** The set being played; the rest are priced from level. */
  live: boolean;
}

export interface TtHandicapOdds extends TtTwoWayOdds {
  /** The line from the first competitor's point of view; negative means they give it away. */
  line: number;
}

export interface TtSetHandicapOdds extends TtTwoWayOdds {
  set: number;
  /** The line on that set's final point margin, from the first competitor's point of view. */
  line: number;
  /** The set being played; the rest are priced from level. */
  live: boolean;
}

/**
 * A pick on a points total. It does not extend {@link TtTwoWayOdds}: the two sides of a total are Over
 * and Under, so neither of them is a competitor to be called A or B.
 */
export interface TtTotalOdds {
  /** The total the final point count is measured against; it carries no sign. */
  line: number;
  probOver: number;
  probUnder: number;
  fairPriceOver: number;
  fairPriceUnder: number;
}

/** A points total scoped to one set, which is the only total there is more than one of. */
export interface TtSetTotalOdds extends TtTotalOdds {
  set: number;
  /** The set being played; the rest are priced from level. */
  live: boolean;
}

/**
 * One ending of the match, as its final score in sets. It does not extend {@link TtTwoWayOdds}: an
 * ending is one pick of four rather than one side of a pair, so it carries a single probability.
 */
export interface TtMatchScoreOdds {
  /** Sets the first competitor takes in this ending. */
  setsA: number;
  /** Sets the second competitor takes in this ending. */
  setsB: number;
  probability: number;
  fairPrice: number;
}

export interface TtRaceOdds extends TtTwoWayOdds {
  set: number;
  /** The points that win the race. */
  target: number;
  /** The set being played; the rest are priced from level. */
  live: boolean;
}

/**
 * A pick on who wins one numbered point of a set. The only market carrying how often it is played at
 * all: every other one on this board is settled by an aggregate that the score always reaches, while a
 * numbered point is only reached if the set runs that long.
 */
export interface TtPointNOdds extends TtTwoWayOdds {
  set: number;
  /** Which point of that set, counting from its first rally. */
  pointNumber: number;
  /** The set being played; the rest are priced from level, where every point is even money. */
  live: boolean;
  /**
   * How often the point is played at all — one minus how often the market refunds. No price is derived
   * from it: the two above are conditional on the point being played, so they sum to one without it.
   */
  probPlayed: number;
}

export interface TtOddEvenOdds {
  probOdd: number;
  probEven: number;
  fairPriceOdd: number;
  fairPriceEven: number;
}

/**
 * The parity of one set's points total. It carries no line — the two picks are the whole market — so it
 * is the set total beside it with the line taken away.
 */
export interface TtSetOddEvenOdds extends TtOddEvenOdds {
  set: number;
  /** The set being played; the rest are priced from level. */
  live: boolean;
}

export interface TtMarketsResponse {
  state: TtDerivedState;
  matchWinner: TtTwoWayOdds;
  setWinner: TtSetOdds[];
  totalOddEven: TtOddEvenOdds;
  /** One entry per line still open; a line the set score has decided is left out, not priced at zero. */
  matchSetsHandicap: TtHandicapOdds[];
  /**
   * One entry per ending the match can still reach. An ending the set score has ruled out is left out
   * rather than priced at zero, so the list shrinks four → three → two as the sets are taken, and it is
   * empty once the match is decided — from there the score is an outcome, not a price. Empty as well
   * outside best-of-3, whose four endings are the only ones this market enumerates.
   */
  matchCorrectScore: TtMatchScoreOdds[];
  /** One entry per race still open; a race a player has already reached the target of is left out. */
  raceToN: TtRaceOdds[];
  /**
   * One entry per numbered point still to be played, per set still to be decided. A point the score has
   * already passed is left out rather than priced: its winner is the rally that was observed, which a
   * score does not carry. A set the match is already past is left out whole, on the same grounds.
   */
  pointNWinner: TtPointNOdds[];
  /**
   * One line per set still to be decided, the one closest to an even market at that set's score. A set
   * both players have taken to within two points of the target is left out: from there it can only end
   * by two, so every line it offers is already decided.
   */
  setPointsHandicap: TtSetHandicapOdds[];
  /**
   * One line per set still to be decided, the one closest to an even market at that set's score. Only a
   * finished set is left out: a total is quoted through the whole deuce, where the handicap above has
   * already gone quiet.
   */
  setTotalPoints: TtSetTotalOdds[];
  /**
   * The points total of the whole match, at the line closest to an even market at this score. A single
   * quote, not a list: there is one such market, so it names no set and none of them is the live one.
   * Null only once the match is over, which is its sole silence — a match total is quoted at every
   * live score, however deep the play runs.
   */
  matchTotalPoints: TtTotalOdds | null;
  /**
   * The parity of each set still to be decided. A set is left out once both players are within two
   * points of the target: from there the set can only end by two, so its total can only be even and
   * there is no parity left to price. That is a set still being played, not a set that has finished —
   * which is why this list goes quiet earlier than `setTotalPoints` on the very same set.
   */
  setTotalOddEven: TtSetOddEvenOdds[];
}

interface TimedResponse<T> {
  value: T;
  durationMillis: number;
}

// Service for the Table Tennis what-if calculator: every market priced from one score
export const ttModelService = {
  getMarkets: (request: TtMarketsRequest): Promise<TimedResponse<TtMarketsResponse>> => {
    return fetch(`${TT_MODEL_BASE_URL}/markets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }).then(async response => {
      if (!response.ok) {
        // the endpoint answers a rejected input with the reason as plain text
        throw new Error(await response.text() || 'Failed to price the table tennis markets');
      }
      return response.json();
    });
  },
};
